import { useEffect, useState } from 'react'
import { History, Loader2, MessageSquare, Archive, Clock3, ChevronRight } from 'lucide-react'
import { getToken } from '@/lib/auth'
import { useAppStore } from '@/store'

interface PersonalSession {
  id: string
  title: string
  project_id: string | null
  created_at: string
  updated_at: string
  archived: number
  message_count: number
  last_message: string | null
  favourite: number
}

async function fetchSessions(archived: boolean): Promise<PersonalSession[]> {
  const token = getToken()
  const response = await fetch('/api/dashboard/sessions?archived=' + (archived ? 'true' : 'false'), {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
  })
  if (!response.ok) throw new Error('Session history unavailable')
  const data = await response.json() as { success?: boolean; sessions?: PersonalSession[] }
  return Array.isArray(data.sessions) ? data.sessions : []
}

function displayTime(value: string) {
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return value
  }
}

export function SessionHistoryPanel() {
  const [active, setActive] = useState<PersonalSession[]>([])
  const [archived, setArchived] = useState<PersonalSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const setActiveSessionId = useAppStore(state => state.setActiveSessionId)
  const setSelectedChatSessionId = useAppStore(state => state.setSelectedChatSessionId)
  const setActiveSection = useAppStore(state => state.setActiveSection)

  useEffect(() => {
    let mounted = true
    Promise.all([
      fetchSessions(false),
      fetchSessions(true),
    ]).then(([currentSessions, archivedSessions]) => {
      if (!mounted) return
      setActive(currentSessions)
      setArchived(archivedSessions)
      setError('')
    }).catch(e => {
      if (!mounted) return
      setActive([])
      setArchived([])
      setError(e instanceof Error ? e.message : 'Session history unavailable')
    }).finally(() => {
      if (mounted) setLoading(false)
    })
    return () => { mounted = false }
  }, [])

  function openSession(session: PersonalSession) {
    setActiveSessionId(session.id)
    setSelectedChatSessionId(session.id)
    setActiveSection('chat')
  }

  const sections = [
    { id: 'current', label: 'Current & recent chats', icon: MessageSquare, sessions: active },
    { id: 'archived', label: 'Archived chats', icon: Archive, sessions: archived },
  ]

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4">
      <div className="rounded-2xl border border-border bg-surface/75 p-5 backdrop-blur-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
              <History className="h-4 w-4 text-accent" />
              My Sessions
            </div>
            <p className="mt-1 text-xs leading-relaxed text-text-muted">
              Your personal OPS chat history. Sessions are scoped to your signed-in account and do not expose other users&apos; workspace history.
            </p>
          </div>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-accent" />}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-3 text-xs text-red-500">
          {error}
        </div>
      )}

      {!loading && !error && active.length === 0 && archived.length === 0 && (
        <div className="rounded-2xl border border-border bg-surface/70 p-8 text-center">
          <History className="mx-auto h-7 w-7 text-text-muted/40" />
          <div className="mt-3 text-sm font-medium text-text-primary">No sessions yet</div>
          <div className="mt-1 text-xs text-text-muted">Your own chats will appear here after you start using OPS.</div>
        </div>
      )}

      {sections.map(section => {
        const Icon = section.icon
        if (!loading && section.sessions.length === 0) return null
        return (
          <section key={section.id} className="rounded-2xl border border-border bg-surface/70 p-4 backdrop-blur-xl">
            <div className="mb-3 flex items-center gap-2">
              <Icon className="h-4 w-4 text-text-muted" />
              <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary">{section.label}</h2>
              <span className="ml-auto rounded-full border border-border bg-bg/40 px-2 py-0.5 text-[10px] text-text-muted">{section.sessions.length}</span>
            </div>
            <div className="space-y-2">
              {section.sessions.map(session => (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => openSession(session)}
                  className="flex w-full items-center gap-3 rounded-xl border border-border/60 bg-bg/30 p-3 text-left transition hover:border-accent/25 hover:bg-surface-elevated/45"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/8 text-accent">
                    <MessageSquare className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-text-primary">{session.title || 'Untitled chat'}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-text-muted">
                      <span className="inline-flex items-center gap-1"><Clock3 className="h-3 w-3" />{displayTime(session.updated_at)}</span>
                      <span>{session.message_count} message{session.message_count === 1 ? '' : 's'}</span>
                      {session.archived === 1 && <span>Archived</span>}
                    </div>
                    {session.last_message && (
                      <div className="mt-1 truncate text-[10px] text-text-muted/80">{session.last_message}</div>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-text-muted/50" />
                </button>
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}
